import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, LoaderCircle, MapPinned, RefreshCw, Settings2, ShieldCheck, WifiOff } from 'lucide-react';
import { AdminSheet } from './components/AdminSheet';
import { FamilyTools } from './components/FamilyTools';
import { GrandpaCallCounter } from './components/GrandpaCallCounter';
import { LocationCard } from './components/LocationCard';
import { MeetupBanner } from './components/MeetupBanner';
import { NotificationSetup } from './components/NotificationSetup';
import { Onboarding } from './components/Onboarding';
import { addLocation, avatarMap, cancelGroupMeetup, createGroupMeetup, getCurrentProfileId, getDashboardState, isApiConfigured, joinFamily, saveQuickNote, setPresence, updateGrandpaCalls } from './lib/api';
import { fileToDataUrl, prepareAvatar } from './lib/image';
import { enablePushNotifications, hasActivePushSubscription, registerServiceWorker } from './lib/push';
import { toSlug } from './lib/time';
import type { DashboardSnapshot, FamilyLocation, FamilyProfile, GrandpaCalls, GroupMeetup, ToastMessage } from './types';

export default function App() {
  const [ownProfile, setOwnProfile] = useState<FamilyProfile | null>(null);
  const [profiles, setProfiles] = useState<FamilyProfile[]>([]);
  const [locations, setLocations] = useState<FamilyLocation[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [groupMeetup, setGroupMeetup] = useState<GroupMeetup | null>(null);
  const [grandpaCalls, setGrandpaCalls] = useState<GrandpaCalls>({ count: 0, date: '' });
  const [isBooting, setIsBooting] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showNotificationSetup, setShowNotificationSetup] = useState(false);
  const [isEnablingPush, setIsEnablingPush] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const showToast = useCallback((text: string, tone: ToastMessage['tone'] = 'info') => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ id: Date.now(), text, tone });
    toastTimerRef.current = window.setTimeout(() => setToast(null), 4200);
  }, []);

  const applySnapshot = useCallback((snapshot: DashboardSnapshot) => {
    setProfiles(snapshot.profiles);
    setLocations(snapshot.locations);
    setAvatarUrls(avatarMap(snapshot.profiles));
    setGroupMeetup(snapshot.groupMeetup || null);
    setGrandpaCalls(snapshot.grandpaCalls || { count: 0, date: '' });
    const currentId = snapshot.currentProfileId || getCurrentProfileId();
    setOwnProfile(snapshot.profiles.find((profile) => profile.id === currentId) || null);
  }, []);

  const loadDashboard = useCallback(async () => {
    const snapshot = await getDashboardState();
    applySnapshot(snapshot);
  }, [applySnapshot]);

  useEffect(() => {
    if (!isApiConfigured) {
      setIsBooting(false);
      return;
    }
    let isMounted = true;
    async function initialize() {
      try {
        await registerServiceWorker();
        await loadDashboard();
        if (isMounted) {
          const hasPush = await hasActivePushSubscription();
          setShowNotificationSetup(!hasPush && localStorage.getItem('locus-push-dismissed') !== 'true');
        }
      } catch (error) {
        if (error instanceof Error && error.message !== 'AUTH_REQUIRED' && isMounted) showToast(error.message, 'error');
      } finally {
        if (isMounted) setIsBooting(false);
      }
    }
    void initialize();
    return () => { isMounted = false; };
  }, [loadDashboard, showToast]);

  useEffect(() => {
    function handleOnline() {
      setIsOffline(false);
      if (ownProfile) void loadDashboard().catch(() => undefined);
    }
    function handleOffline() { setIsOffline(true); }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadDashboard, ownProfile]);

  useEffect(() => {
    if (!ownProfile) return;
    const refresh = () => {
      if (navigator.onLine && document.visibilityState === 'visible') void loadDashboard().catch(() => undefined);
    };
    const timer = window.setInterval(refresh, 12_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadDashboard, ownProfile]);

  async function handleOnboarding(details: { displayName: string; familyCode: string; photo: File | null }) {
    setIsSubmitting(true);
    setOnboardingError(null);
    try {
      const avatar = details.photo ? await prepareAvatar(details.photo) : null;
      const result = await joinFamily({
        familyCode: details.familyCode,
        displayName: details.displayName,
        avatarDataUrl: avatar ? await fileToDataUrl(avatar) : undefined,
      });
      setOwnProfile(result.profile);
      await loadDashboard();
      setShowNotificationSetup(true);
      showToast(result.isNewProfile ? 'Profilo creato: benvenuto in L.O.C.U.S.!' : `Dispositivo collegato a ${result.profile.display_name}.`, 'success');
    } catch (error) {
      setOnboardingError(error instanceof Error ? error.message : 'Impossibile completare l’accesso.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleMovePerson(profileId: string, locationId: string) {
    if (!ownProfile || !locationId) return;
    setIsUpdating(true);
    const previousProfiles = profiles;
    const changedAt = new Date().toISOString();
    const target = profiles.find((profile) => profile.id === profileId);
    const isLocationChanging = target?.current_location_id !== locationId;
    setProfiles((current) => current.map((profile) => profile.id === profileId
      ? {
        ...profile,
        current_location_id: locationId,
        note_text: isLocationChanging ? '' : profile.note_text,
        last_seen_at: changedAt,
      }
      : profile));
    try {
      await setPresence(profileId, locationId);
      const location = locations.find((entry) => entry.id === locationId);
      showToast(profileId === ownProfile.id ? `Ora sei ${location?.name.toLowerCase()}.` : `${target?.display_name} spostato/a in ${location?.name}.`, 'success');
      await loadDashboard();
    } catch (error) {
      setProfiles(previousProfiles);
      showToast(error instanceof Error ? error.message : 'Posizione non aggiornata.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleAddLocation(name: string, emoji: string) {
    if (!ownProfile || !name) return;
    setIsUpdating(true);
    try {
      await addLocation({ name, emoji, slug: toSlug(name) });
      await loadDashboard();
      showToast(`${name} aggiunto ai luoghi.`, 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Luogo non aggiunto.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleSaveNote(noteText: string) {
    setIsUpdating(true);
    try {
      await saveQuickNote(noteText);
      await loadDashboard();
      showToast(noteText ? 'Nota aggiornata.' : 'Nota rimossa.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Nota non aggiornata.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleCreateMeetup(locationId: string, scheduledFor: string) {
    setIsUpdating(true);
    try {
      await createGroupMeetup(locationId, scheduledFor);
      await loadDashboard();
      showToast('Ritrovo creato e famiglia avvisata.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Ritrovo non creato.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleCancelMeetup() {
    setIsUpdating(true);
    try {
      await cancelGroupMeetup();
      await loadDashboard();
      showToast('Ritrovo annullato.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Ritrovo non annullato.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleUpdateGrandpaCalls(action: 'increment' | 'set', count?: number) {
    setIsUpdating(true);
    try {
      const updated = await updateGrandpaCalls(action, count);
      setGrandpaCalls(updated);
      showToast(action === 'increment' ? 'Chiamata del Nonno aggiunta! 📞' : 'Contatore aggiornato.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Contatore non aggiornato.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleEnablePush() {
    if (!ownProfile) return;
    setIsEnablingPush(true);
    try {
      await enablePushNotifications(ownProfile.id);
      setShowNotificationSetup(false);
      localStorage.removeItem('locus-push-dismissed');
      showToast('Notifiche attivate.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Notifiche non attivate.', 'error');
    } finally {
      setIsEnablingPush(false);
    }
  }

  const peopleByLocation = useMemo(() => {
    const grouped = new Map<string, FamilyProfile[]>();
    profiles.forEach((profile) => {
      if (!profile.current_location_id) return;
      grouped.set(profile.current_location_id, [...(grouped.get(profile.current_location_id) || []), profile]);
    });
    return grouped;
  }, [profiles]);

  if (isBooting) {
    return (
      <main className="full-state" aria-busy="true" aria-live="polite">
        <img className="full-state__theo-logo" src="/assets/theo-logo.png" width="72" height="72" alt="T.H.E.O." />
        <LoaderCircle className="spin" size={40} aria-hidden="true" />
        <strong>Avvio di L.O.C.U.S.</strong>
      </main>
    );
  }

  if (!isApiConfigured) {
    return (
      <main className="full-state full-state--setup">
        <img src="/icons/locus-icon-v2-192.png" width="84" height="84" alt="" />
        <h1>L.O.C.U.S. è pronto</h1>
        <p>Manca solo l’indirizzo del backend. Configura <code>VITE_API_URL</code> e ricompila l’app.</p>
      </main>
    );
  }

  if (!ownProfile) return <Onboarding isSubmitting={isSubmitting} error={onboardingError} onSubmit={handleOnboarding} />;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-lockup">
          <img src="/icons/locus-icon-v2-192.png" width="48" height="48" alt="" />
          <div>
            <p className="eyebrow">Amaika · Calella</p>
            <p className="app-header__name">L.O.C.U.S.</p>
            <p className="app-header__expanded-name">Loved Ones Coordination &amp; Updates System</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="icon-button" type="button" onClick={() => setShowNotificationSetup(true)} aria-label="Gestisci notifiche"><Bell /></button>
          {ownProfile.is_admin && <button className="icon-button icon-button--admin" type="button" onClick={() => setShowAdmin(true)} aria-label="Apri pannello amministratore"><Settings2 /></button>}
        </div>
      </header>

      <main className="dashboard">
        {isOffline && <p className="offline-banner" role="status"><WifiOff size={17} /> Sei offline: mostro l’ultimo aggiornamento disponibile.</p>}
        <section className="dashboard-hero" aria-labelledby="dashboard-title">
          <span className="dashboard-hero__icon"><MapPinned size={25} /></span>
          <div><p className="eyebrow">Presenza famiglia</p><h1 id="dashboard-title">Dove siamo adesso?</h1><p>Tocca un luogo per aggiornare la tua posizione.</p></div>
          <button className="icon-button dashboard-hero__refresh" type="button" onClick={() => void loadDashboard()} aria-label="Aggiorna dati"><RefreshCw size={20} /></button>
        </section>

        {groupMeetup && <MeetupBanner meetup={groupMeetup} />}

        <GrandpaCallCounter
          grandpaCalls={grandpaCalls}
          isAdmin={ownProfile.is_admin}
          isBusy={isUpdating || isOffline}
          onIncrement={() => handleUpdateGrandpaCalls('increment')}
          onSetCount={(count) => handleUpdateGrandpaCalls('set', count)}
        />

        {showNotificationSetup && (
          <NotificationSetup
            isBusy={isEnablingPush}
            onEnable={() => void handleEnablePush()}
            onDismiss={() => { setShowNotificationSetup(false); localStorage.setItem('locus-push-dismissed', 'true'); }}
          />
        )}

        {ownProfile.is_admin && <button className="admin-shortcut" type="button" onClick={() => setShowAdmin(true)}><ShieldCheck size={18} /> Modalità admin: puoi spostare tutti</button>}

        <section className="places-grid" aria-label="Luoghi disponibili">
          {locations.map((location) => (
            <LocationCard
              key={location.id}
              location={location}
              people={peopleByLocation.get(location.id) || []}
              avatarUrls={avatarUrls}
              isCurrent={ownProfile.current_location_id === location.id}
              isUpdating={isUpdating || isOffline}
              onChoose={(locationId) => void handleMovePerson(ownProfile.id, locationId)}
            />
          ))}
        </section>

        <FamilyTools
          ownProfile={ownProfile}
          isBusy={isUpdating || isOffline}
          onSaveNote={handleSaveNote}
        />
      </main>

      <footer className="app-footer"><img src="/assets/theo-logo.png" width="26" height="26" alt="" /><span>Powered by T.H.E.O.</span></footer>

      {showAdmin && ownProfile.is_admin && (
        <AdminSheet
          profiles={profiles}
          locations={locations}
          groupMeetup={groupMeetup}
          isBusy={isUpdating}
          onClose={() => setShowAdmin(false)}
          onMovePerson={handleMovePerson}
          onAddLocation={handleAddLocation}
          onCreateMeetup={handleCreateMeetup}
          onCancelMeetup={handleCancelMeetup}
        />
      )}
      {toast && <p key={toast.id} className={`toast toast--${toast.tone}`} role="status">{toast.text}</p>}
    </div>
  );
}
